import { lazy, Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { errorMessageKey } from '../../api/api-error';
import { paths } from '../../app/paths';
import { useMe } from '../../auth/session';
import { AuthDialog } from '../../components/auth/auth-dialog';
import { Spinner } from '../../components/ui/spinner';
import { useI18n } from '../../i18n/i18n';
import { readPendingInvitation } from '../../lib/pending-invitation';
import {
  LAST_WORKSPACE_ATTEMPTS,
  LAST_WORKSPACE_KEY,
  useLastWorkspace,
} from '../../workspaces/workspaces-api';
// Лендинг со сценами — отдельным чанком: вошедшему он не нужен, приложению — тоже.
const Landing = lazy(() => import('./landing').then((module) => ({ default: module.Landing })));

/** Формы, которые вошедшему не нужны: с них — в приложение. */
const GUEST_ONLY = new Set(['/login', '/login/second-factor', '/register', '/forgot-password']);

/**
 * Гостевая часть (docs/saas/screens/лендинг.md): лендинг, а гостевые формы — окном поверх него
 * на своих адресах (адреса из писем и редиректов API сохранены). Закрыть окно — уйти на `/`.
 * Вошедшего `/` ведёт в последнее пространство.
 */
export function GuestShell() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = me.data !== null && me.data !== undefined;
  const atRoot = location.pathname === paths.root;

  if (me.isPending) return <FullScreenSpinner />;
  if (signedIn && GUEST_ONLY.has(location.pathname)) return <Navigate to={paths.root} replace />;
  if (signedIn && atRoot) {
    // Пришёл по ссылке приглашения, а потом регистрировался и входил: вернуть к приглашению.
    if (readPendingInvitation() !== null) return <Navigate to="/invitations/accept" replace />;
    return <LastWorkspaceRedirect />;
  }
  return (
    <>
      <Suspense fallback={<div className="min-h-dvh bg-background" />}>
        <Landing signedIn={signedIn} />
      </Suspense>
      {!atRoot && (
        <AuthDialog onClose={() => void navigate(paths.root)}>
          <Outlet />
        </AuthDialog>
      )}
    </>
  );
}

function FullScreenSpinner() {
  const { t } = useI18n();
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner label={t('common.loading')} />
    </div>
  );
}

function LastWorkspaceRedirect() {
  const { t } = useI18n();
  const last = useLastWorkspace();
  const attempts = useQueryClient().getQueryState(LAST_WORKSPACE_KEY)?.dataUpdateCount ?? 0;
  const workspace = last.data?.workspace;
  if (workspace !== undefined && workspace !== null) {
    return <Navigate to={paths.workspace(workspace.id)} replace />;
  }
  return (
    <div className="grid min-h-dvh place-items-center px-6 text-center text-sm text-muted-foreground">
      {last.isError ? (
        <p className="text-danger">{t(errorMessageKey(last.error))}</p>
      ) : workspace === null && attempts >= LAST_WORKSPACE_ATTEMPTS ? (
        <p>
          {t('workspaces.notReady')}{' '}
          <Link to={paths.workspaces} className="text-primary hover:underline">
            {t('workspaces.toList')}
          </Link>
        </p>
      ) : (
        <p>{t('workspaces.preparing')}</p>
      )}
    </div>
  );
}
